import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const HomeBelow = lazy(() => import('./HomeBelow'));

const unsplashSrc = (id, w, q) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=${q}`;

const unsplashSrcSet = (id) =>
  [
    `${unsplashSrc(id, 640, 45)} 640w`,
    `${unsplashSrc(id, 828, 50)} 828w`,
    `${unsplashSrc(id, 1080, 55)} 1080w`,
    `${unsplashSrc(id, 1280, 58)} 1280w`,
  ].join(', ');

const Home = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [loadedSlides, setLoadedSlides] = useState(() => new Set([0]));
  const [loadBelow, setLoadBelow] = useState(false);

  const heroSlides = [
    {
      badge: (
        <>
          Pague menos na sua fatura de energia com a ener360{' '}
          <img
            src='/logo-ener-green.svg'
            alt='ener360'
            className='hero-badge-logo'
            width={56}
            height={10}
            decoding='async'
          />
        </>
      ),
      title: 'Economia e sustentabilidade, sem investimento ou obra',
      description:
        'A ener360 é sua empresa na transição energética: selecione, compare e contrate a economia 100% online.',
      cta: {
        label: 'Simular economia',
        to: '/contato',
        learnMoreTo: '/fluxo-comercializacao',
      },
      photo: 'photo-1509391366360-2e959784a276',
    },
    {
      badge: 'Seja SOU ENER360 - Licenciado',
      title: 'Venda energia. Ganhe como licenciado ener360',
      description:
        'Transforme sua rede de contatos em renda extra representando a ener360, com todo o suporte comercial e treinamentos do nosso time.',
      cta: {
        label: 'Quero ser licenciado',
        to: '/sou-ener360#formulario-licenciado',
        learnMoreTo: '/sou-ener360',
      },
      photo: 'photo-1521737711867-e3b97375f902',
    },
    {
      badge: 'Clube ener360',
      title: 'Sua indicação vale dinheiro no seu bolso',
      description:
        'Indique clientes para a ener360 e seja pago por cada nova adesão confirmada.',
      cta: {
        label: 'Indique & Ganhe',
        to: '/clube-ener360#formulario-indicacao',
        learnMoreTo: '/clube-ener360',
      },
      photo: 'photo-1554224155-6726b3ff858f',
    },
    {
      badge: 'Seguros',
      title: 'Proteja seu patrimônio com o seguro certo',
      description:
        'Soluções de seguro pensadas para proteger seus equipamentos e seu projeto de energia do início ao fim.',
      cta: {
        label: 'Simular Seguro',
        to: '/seguro-consorcio-financiamento#formulario-cotacao',
        learnMoreTo: '/seguro-consorcio-financiamento',
      },
      photo: 'photo-1450101499163-c8848c66ca85',
    },
    {
      badge: 'Investimento',
      title: 'Consórcio e financiamento para o seu projeto de energia',
      description:
        'Planeje sua conquista com consórcio sem juros ou financiamento facilitado, sob medida para sua transição energética.',
      cta: {
        label: 'Simular Investimento',
        to: '/seguro-consorcio-financiamento#formulario-cotacao',
        learnMoreTo: '/seguro-consorcio-financiamento',
      },
      photo: 'photo-1554774853-719586f82d77',
    },
    {
      badge: 'Consultoria',
      title: 'Consultoria especializada no mercado de energia',
      description:
        'Seu especialista na análise de soluções energéticas e mercado livre de energia que gera mais segurança, assertividade e resultados para sua organização.',
      cta: {
        label: 'Falar com consultor',
        to: '/consultoria-energia#formulario-consultoria',
        learnMoreTo: '/consultoria-energia',
      },
      photo: 'photo-1454165804606-c3d57bc86b40',
    },
  ];

  const slideCount = heroSlides.length;

  const nextSlide = useCallback(() => {
    setActiveSlide((prev) => (prev + 1) % slideCount);
  }, [slideCount]);

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + slideCount) % slideCount);
  };

  useEffect(() => {
    if (isPaused) return undefined;
    const timer = setInterval(nextSlide, 5200);
    return () => clearInterval(timer);
  }, [nextSlide, activeSlide, isPaused]);

  useEffect(() => {
    setLoadedSlides((prev) => {
      if (prev.has(activeSlide)) return prev;
      const next = new Set(prev);
      next.add(activeSlide);
      return next;
    });
  }, [activeSlide]);

  useEffect(() => {
    setLoadBelow(true);
  }, []);

  useEffect(() => {
    if (!loadBelow || window.location.hash !== '#simulacao') return undefined;
    let cancelled = false;
    const scrollToForm = (attempts = 40) => {
      if (cancelled) return;
      const target = document.getElementById('simulacao');
      if (target) {
        target.scrollIntoView({ block: 'center', behavior: 'auto' });
        return;
      }
      if (attempts > 0) {
        window.requestAnimationFrame(() => scrollToForm(attempts - 1));
      }
    };
    scrollToForm();
    return () => {
      cancelled = true;
    };
  }, [loadBelow]);

  const pauseCarousel = () => setIsPaused(true);
  const resumeCarousel = () => setIsPaused(false);

  const showSimulationForm = () => {
    setLoadBelow(true);
    const scrollToForm = (attempts = 24) => {
      const target = document.getElementById('simulacao');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (attempts > 0) {
        window.requestAnimationFrame(() => scrollToForm(attempts - 1));
      }
    };
    window.requestAnimationFrame(() => scrollToForm());
  };

  return (
    <div className='home'>
      <section
        className='hero'
        onMouseDown={pauseCarousel}
        onMouseUp={resumeCarousel}
        onMouseLeave={resumeCarousel}
        onTouchStart={pauseCarousel}
        onTouchEnd={resumeCarousel}
      >
        {heroSlides.map((slide, index) => (
          <div
            key={slide.photo}
            className={`hero-slide ${
              index === activeSlide ? 'hero-slide--active' : ''
            }`}
          >
            {loadedSlides.has(index) && (
              <img
                className='hero-slide-img'
                src={unsplashSrc(slide.photo, 828, 50)}
                srcSet={unsplashSrcSet(slide.photo)}
                sizes='100vw'
                alt=''
                width={828}
                height={532}
                decoding='async'
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            )}
            <div className='hero-overlay'></div>
          </div>
        ))}

        <div className='hero-story-bar'>
          {heroSlides.map((slide, index) => (
            <button
              key={slide.photo}
              className={`hero-story-seg ${
                index < activeSlide ? 'hero-story-seg--done' : ''
              } ${index === activeSlide ? 'hero-story-seg--active' : ''} ${
                isPaused && index === activeSlide ? 'hero-story-seg--paused' : ''
              }`}
              onClick={() => setActiveSlide(index)}
              aria-label={`Ir para slide ${index + 1}`}
            >
              <i></i>
            </button>
          ))}
        </div>

        <button
          className='hero-nav-zone hero-nav-zone--left'
          onClick={prevSlide}
          aria-label='Slide anterior'
        ></button>
        <button
          className='hero-nav-zone hero-nav-zone--right'
          onClick={nextSlide}
          aria-label='Próximo slide'
        ></button>

        <div className='container'>
          <div className='hero-content'>
            <span className='hero-badge fade-in-up'>
              {heroSlides[activeSlide].badge}
            </span>
            <h1 className='hero-title fade-in-up'>
              {heroSlides[activeSlide].title}
            </h1>
            <p className='hero-description fade-in-up'>
              {heroSlides[activeSlide].description}
            </p>
            <br />
            <div className='hero-actions fade-in-up'>
              {activeSlide === 0 ? (
                <button
                  type='button'
                  className='btn btn-primary btn-lg'
                  onClick={showSimulationForm}
                >
                  {heroSlides[activeSlide].cta.label}
                </button>
              ) : (
                <Link
                  to={heroSlides[activeSlide].cta.to}
                  className='btn btn-primary btn-lg'
                >
                  {heroSlides[activeSlide].cta.label}
                </Link>
              )}
              <Link
                to={heroSlides[activeSlide].cta.learnMoreTo}
                className='btn btn-secondary btn-lg'
              >
                Conhecer Mais
              </Link>
            </div>
          </div>
        </div>
      </section>

      {loadBelow && (
        <Suspense fallback={null}>
          <HomeBelow onSimulate={showSimulationForm} />
        </Suspense>
      )}
    </div>
  );
};

export default Home;
